import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/imageProcessing";
import {
  ArrowLeft, Camera, Sparkle, ShieldCheck, ShieldWarning, Trash, ArrowRight, ArrowUpRight,
  Spinner, MagnifyingGlass, CheckCircle, WarningCircle, Storefront, CurrencyDollar, CaretDown, CaretUp, ShareNetwork,
  Crown, LockSimple, Lightbulb, ImageSquare,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const AUTH_META = {
  "looks authentic": { cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]", Icon: ShieldCheck },
  "some reproduction risk": { cls: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]", Icon: ShieldWarning },
  "high reproduction risk": { cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]", Icon: ShieldWarning },
  uncertain: { cls: "bg-[#F1EFE9] text-[#57534E] border-[#D6D3CD]", Icon: ShieldWarning },
};
const authMeta = (a) => AUTH_META[(a || "uncertain").toLowerCase()] || AUTH_META.uncertain;

const CONF_CLS = {
  High: "bg-[#DCFCE7] text-[#166534]", Medium: "bg-[#FEF3C7] text-[#92400E]", Low: "bg-[#F1EFE9] text-[#57534E]",
};

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// Fetch the branded share card and either native-share it (mobile) or download it.
async function shareFindImage(id, title) {
  try {
    const { data: blob } = await api.get(`/finds/${id}/share-card.png`, { responseType: "blob" });
    const file = new File([blob], "fantastic-find.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "My Fantastic Find", text: `${title} — scored on Vintage Vantage` });
      } catch (e) {
        if (e?.name !== "AbortError") throw e;
      }
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "fantastic-find.png";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Image saved — post it to Instagram or Facebook! 📲");
    }
  } catch {
    toast.error("Couldn't build the share image.");
  }
}

export default function FantasticFinds() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const libRef = useRef(null);
  const addRef = useRef(null);
  const [finds, setFinds] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [addingFor, setAddingFor] = useState(null); // find id we're adding an angle to
  const [analyzingId, setAnalyzingId] = useState(null);
  const [buyFor, setBuyFor] = useState(null); // find pending "bought it" price entry
  const [buyPrice, setBuyPrice] = useState("");
  const [promoteFor, setPromoteFor] = useState(null); // find id pending promote-to-drafts
  const [chairishRef, setChairishRef] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [grabbing, setGrabbing] = useState(false);
  const [shareFor, setShareFor] = useState(null); // find pending share-to-gallery
  const [shareCaption, setShareCaption] = useState("");
  const [shareName, setShareName] = useState("");
  const [sharing, setSharing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/finds");
      setFinds(data.finds || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    try {
      const compressed = await compressImage(file, 1600, 0.82);
      const dataUrl = await fileToDataUrl(compressed);
      const { data } = await api.post("/finds/identify", { image_base64: dataUrl, mime_type: "image/jpeg" });
      setFinds((prev) => [data, ...prev]);
      toast.success("Got it — here's what we found.");
      setTimeout(() => document.getElementById(`find-card-${data.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      toast.error("Couldn't analyze that photo — try another angle with good light.");
    } finally {
      setScanning(false);
    }
  };

  const startAddAngle = (id) => { setAddingFor(id); addRef.current?.click(); };

  const onAddAngle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const id = addingFor;
    if (!file || !id) return;
    setAnalyzingId(id);
    try {
      const compressed = await compressImage(file, 1600, 0.82);
      const dataUrl = await fileToDataUrl(compressed);
      const { data } = await api.post(`/finds/${id}/add-photo`, { image_base64: dataUrl, mime_type: "image/jpeg" });
      setFinds((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success("Sharpened with the new angle.");
    } catch {
      toast.error("Couldn't add that photo — try again with good light.");
    } finally {
      setAnalyzingId(null);
      setAddingFor(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this find?")) return;
    try {
      await api.delete(`/finds/${id}`);
      setFinds((prev) => prev.filter((f) => f.id !== id));
      toast.success("Find removed.");
    } catch { toast.error("Couldn't remove that."); }
  };

  const rateFind = async (id, verdict, note) => {
    try {
      const { data } = await api.post(`/finds/${id}/rating`, { verdict, note: note || "" });
      setFinds((prev) => prev.map((f) => (f.id === id ? data : f)));
      toast.success("Thanks — your call shapes future Finder scans.");
      return true;
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Rating the Finder is a Member perk.");
      } else {
        toast.error("Couldn't save your rating — try again.");
      }
      return false;
    }
  };

  const refineByMaker = async (id, maker) => {
    try {
      const { data } = await api.post(`/finds/${id}/refine-by-maker`, { maker });
      setFinds((prev) => prev.map((f) => (f.id === id ? data : f)));
      toast.success(`Sharpened — pinned to "${maker}".`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refine — try again.");
      return false;
    }
  };

  const confirmGrab = async () => {
    const f = buyFor;
    if (!f) return;
    setGrabbing(true);
    try {
      // 1) Log the acquisition (optional price — powers the profit/flip story).
      await api.post(`/finds/${f.id}/purchased`, { purchase_price: buyPrice ? Number(buyPrice) : null });
      // 2) Send it straight to Drafts (optional Chairish match auto-fills the details).
      const { data: promo } = await api.post(`/finds/${f.id}/promote`, { chairish_ref: chairishRef.trim() || null });
      const lid = promo.listing_id;
      if (promo.enrich_ready) {
        toast.loading("Auto-filling the details from Chairish…", { id: "enrich" });
        try {
          await api.post(`/listings/${lid}/enrich?pull_images=false`);
          toast.success("Details filled in from Chairish — just add your photos, price & shipping.", { id: "enrich" });
        } catch {
          toast.error("Saved to Drafts, but couldn't reach that Chairish page — you can enrich it in the editor.", { id: "enrich" });
        }
      } else {
        toast.success("Fantastic — it's in your Drafts. Let's finish the listing! 🎉");
      }
      setBuyFor(null); setBuyPrice(""); setChairishRef("");
      navigate(`/review/${lid}?fromfind=1`);
    } catch {
      toast.error("Couldn't put that in your Drafts — try again.");
    } finally {
      setGrabbing(false);
    }
  };

  const openShare = (f) => {
    setShareCaption("");
    setShareName(user?.name || "");
    setShareFor(f);
  };

  const confirmShare = async () => {
    const f = shareFor;
    setSharing(true);
    try {
      const { data } = await api.post(`/finds/${f.id}/share`, { caption: shareCaption.trim(), display_name: shareName.trim() });
      setFinds((prev) => prev.map((x) => (x.id === f.id ? data : x)));
      toast.success("Shared to Fantastic Finds — nice brag! 🎉");
      setShareFor(null);
    } catch { toast.error("Couldn't share that."); } finally { setSharing(false); }
  };

  const unshare = async (id) => {
    try {
      const { data } = await api.post(`/finds/${id}/unshare`);
      setFinds((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success("Removed from the gallery.");
    } catch { toast.error("Couldn't update that."); }
  };

  const confirmPromote = async () => {
    const id = promoteFor;
    setPromoting(true);
    try {
      const { data } = await api.post(`/finds/${id}/promote`, { chairish_ref: chairishRef.trim() || null });
      setFinds((prev) => prev.map((x) => (x.id === id ? { ...x, promoted_listing_id: data.listing_id, status: "promoted" } : x)));
      const lid = data.listing_id;
      if (data.enrich_ready) {
        toast.loading("Auto-filling the details from Chairish…", { id: "enrich" });
        try {
          await api.post(`/listings/${lid}/enrich?pull_images=false`);
          toast.success("Details filled in from Chairish — just add your photos, price & shipping.", { id: "enrich" });
        } catch {
          toast.error("Sent to Drafts, but couldn't reach that Chairish page — you can enrich it in the editor.", { id: "enrich" });
        }
      } else {
        toast.success("Sent to Drafts — let's finish the listing.");
      }
      setPromoteFor(null); setChairishRef("");
      navigate(`/review/${lid}?fromfind=1`);
    } catch {
      toast.error("Couldn't send to Drafts.");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="finds-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage" onClick={() => navigate("/dashboard")} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#E5E0D6] cursor-pointer shrink-0 hover:ring-[#B47A3C] transition-all" data-testid="app-compass-mark" title="Vintage Vantage — home" />
            <button onClick={() => navigate("/dashboard")} data-testid="finds-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
              <ArrowLeft size={18} weight="bold" /> Dashboard
            </button>
          </div>
          <button onClick={() => navigate("/finds/community")} data-testid="finds-gallery-link" className="text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420] inline-flex items-center gap-1.5">
            <Sparkle size={14} weight="fill" /> Community gallery
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 pb-24 sm:pb-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={22} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">On the hunt</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">Fantastic Finds</h1>
        <p className="text-lg text-[#525252] leading-relaxed mb-6">
          Spotted something in the wild? Snap a photo to identify the piece, spot reproductions, and get an
          instant "should I flip it?" call — right there in the aisle. Bought one? One tap puts it in your Drafts, ready to finish.
        </p>

        {/* Capture */}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" data-testid="finds-capture-input" />
        <input ref={libRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="finds-library-input" />
        <input ref={addRef} type="file" accept="image/*" capture="environment" onChange={onAddAngle} className="hidden" data-testid="finds-addangle-input" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={scanning}
            data-testid="finds-capture-button"
            className="w-full h-16 bg-[#0D1B2A] text-[#FDFBF7] rounded-xl font-semibold text-lg inline-flex items-center justify-center gap-3 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-70 shadow-md"
          >
            {scanning ? <><Spinner size={22} className="animate-spin" /> Identifying…</>
              : <><Camera size={24} weight="fill" /> Take a photo</>}
          </button>
          <button
            onClick={() => libRef.current?.click()}
            disabled={scanning}
            data-testid="finds-library-button"
            className="w-full h-16 border border-[#0D1B2A] text-[#0D1B2A] rounded-xl font-semibold text-lg inline-flex items-center justify-center gap-3 hover:bg-[#FBF7F0] transition-transform active:scale-[0.99] disabled:opacity-70"
          >
            <ImageSquare size={24} weight="fill" /> Choose from library
          </button>
        </div>
        <p className="text-xs text-[#8A867D] mt-2 text-center">
          Uses AI vision on your account. Take it straight-on in good light for the best read. Guidance, not an appraisal.
        </p>

        <div className="mt-5 border border-[#E5DFD2] bg-[#FBF7F0] rounded-xl p-4" data-testid="finds-workflow-note">
          <p className="font-semibold text-[#0D1B2A] mb-1.5 flex items-center gap-2">
            <Lightbulb size={18} weight="fill" className="text-[#B47A3C]" /> Make every scan do double duty
          </p>
          <p className="text-sm text-[#525252] leading-relaxed">
            These photos are for <strong>identification and decision-making support</strong> — they help you decide if a
            piece is worth buying, right there in the aisle. When you decide to buy, send your fantastic find
            <strong> straight to Drafts</strong>. Then, once you've taken your first quality photo of the piece
            (<strong>all pieces included</strong>), <strong>re-scan it with the camera</strong> — you'll get an even
            more accurate description <em>and</em> a ready-to-use <strong>cover photo</strong> in the process.
          </p>
        </div>

        {/* Finds list */}
        <div className="mt-10 space-y-6">
          {finds.length === 0 && !scanning ? (
            <div className="border border-dashed border-[#D6D3CD] rounded-xl p-10 text-center bg-[#FBFAF6]" data-testid="finds-empty">
              <MagnifyingGlass size={34} weight="light" className="mx-auto text-[#B8B2A6] mb-3" />
              <p className="text-[#8A867D]">No finds yet. Scan your first piece to see what it is and whether it's worth flipping.</p>
            </div>
          ) : null}
          {finds.map((f) => <FindCard key={f.id} f={f} onDelete={remove} onBought={(x) => { setChairishRef(""); setBuyPrice(""); setBuyFor(x); }} onPromote={(id) => { setChairishRef(""); setPromoteFor(id); }} onShare={openShare} onUnshare={unshare} onAddAngle={startAddAngle} onRate={rateFind} onRefineByMaker={refineByMaker} analyzing={analyzingId === f.id} navigate={navigate} />)}
        </div>
      </main>

      {/* Fantastic-find-acquired → Drafts dialog: logs the buy + sends it straight to Drafts */}
      <Dialog open={!!buyFor} onOpenChange={(o) => { if (!o && !grabbing) { setBuyFor(null); setBuyPrice(""); setChairishRef(""); } }}>
        <DialogContent className="max-w-md" data-testid="finds-bought-dialog">
          <DialogHeader><DialogTitle>{buyFor?.__alreadyMine ? "It's yours — let's draft it! 🎉" : "Fantastic find acquired! 🎉"}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252] -mt-1">We'll drop it straight into your <strong>Drafts</strong>, pre-filled from the scan, ready to finish.</p>

          <label className="label-caps block mt-3 mb-1.5">What did you pay? (optional — tracks your profit)</label>
          <div className="relative">
            <CurrencyDollar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A867D]" />
            <input
              type="number" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)}
              placeholder="e.g. 180" data-testid="finds-bought-price"
              className="w-full h-11 pl-9 pr-3 rounded-md border border-[#D6D3CD] bg-white text-base focus:outline-none focus:border-[#0D1B2A]"
            />
          </div>

          <button onClick={confirmGrab} disabled={grabbing} data-testid="finds-bought-confirm"
            className="mt-4 w-full h-12 bg-[#0D1B2A] text-[#FDFBF7] rounded-md font-semibold inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-70">
            {grabbing ? <><Spinner size={16} className="animate-spin" /> Putting it in your Drafts…</> : <><ArrowRight size={16} weight="bold" /> Put it in my Drafts</>}
          </button>
        </DialogContent>
      </Dialog>

      {/* Send-to-Drafts dialog with optional Chairish match auto-fill */}
      <Dialog open={!!promoteFor} onOpenChange={(o) => { if (!o && !promoting) { setPromoteFor(null); setChairishRef(""); } }}>
        <DialogContent className="max-w-md" data-testid="finds-promote-dialog">
          <DialogHeader><DialogTitle>Send to Drafts</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252] -mt-1">
            We'll pre-fill the listing from the scan. Found the <strong>exact match on Chairish</strong>? Paste the link and
            we'll auto-fill the full description, dimensions, materials &amp; condition — you just add your photos, price &amp; shipping.
          </p>
          <label className="label-caps block mt-3 mb-1.5">Chairish listing link (optional)</label>
          <input
            value={chairishRef} onChange={(e) => setChairishRef(e.target.value)}
            placeholder="https://www.chairish.com/product/…" data-testid="finds-promote-chairish"
            className="w-full h-11 px-3 rounded-md border border-[#D6D3CD] bg-white text-base focus:outline-none focus:border-[#0D1B2A]"
          />
          <button onClick={confirmPromote} disabled={promoting} data-testid="finds-promote-confirm"
            className="mt-4 w-full h-11 bg-[#0D1B2A] text-[#FDFBF7] rounded-md font-semibold inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-70">
            {promoting ? <><Spinner size={16} className="animate-spin" /> Sending…</> : <><ArrowRight size={16} weight="bold" /> Send to Drafts</>}
          </button>
        </DialogContent>
      </Dialog>

      {/* Share-to-gallery dialog (auto-prompted after buying) */}
      <Dialog open={!!shareFor} onOpenChange={(o) => { if (!o && !sharing) setShareFor(null); }}>
        <DialogContent className="max-w-md" data-testid="finds-share-dialog">
          <DialogHeader><DialogTitle>Show off your score 🎉</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252] -mt-1">
            Post this to the public <strong>Fantastic Finds</strong> gallery — a little brag, a little inspiration.
            We'll show the photo, what it is, era &amp; maker. <strong>No prices, ever.</strong>
          </p>
          <label className="label-caps block mt-3 mb-1.5">Your caption (optional)</label>
          <textarea
            value={shareCaption} onChange={(e) => setShareCaption(e.target.value)} rows={2} maxLength={280}
            placeholder="Found this beauty at an estate sale for a steal…" data-testid="finds-share-caption"
            className="w-full px-3 py-2 rounded-md border border-[#D6D3CD] bg-white text-base focus:outline-none focus:border-[#0D1B2A] resize-none"
          />
          <label className="label-caps block mt-3 mb-1.5">Show as</label>
          <input
            value={shareName} onChange={(e) => setShareName(e.target.value)}
            placeholder="A Vintage Vantage seller" data-testid="finds-share-name"
            className="w-full h-11 px-3 rounded-md border border-[#D6D3CD] bg-white text-base focus:outline-none focus:border-[#0D1B2A]"
          />
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShareFor(null)} data-testid="finds-share-skip"
              className="flex-1 h-11 border border-[#D6D3CD] rounded-md font-semibold hover:bg-[#F5F3ED] transition-colors">
              Not now
            </button>
            <button onClick={confirmShare} disabled={sharing} data-testid="finds-share-confirm"
              className="flex-1 h-11 bg-[#0D1B2A] text-[#FDFBF7] rounded-md font-semibold inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-70">
              {sharing ? <><Spinner size={16} className="animate-spin" /> Sharing…</> : <><Sparkle size={16} weight="fill" /> Share to gallery</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Chips({ items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((t, i) => <span key={i} className="text-xs bg-[#F1EFE9] text-[#57534E] px-2 py-0.5 rounded-full">{t}</span>)}
    </div>
  );
}

// Build a focused marketplace search query from the identification.
function marketQuery(id) {
  const rawMaker = id.likely_designer && !/not identified|unknown|^\s*$/i.test(id.likely_designer)
    ? id.likely_designer
    : (id.possible_makers?.[0] || "");
  const maker = rawMaker
    .replace(/\(.*?\)/g, "")
    .replace(/possibly|probably|likely|attributed to|in the style of|style of|maker unknown|unknown/gi, "")
    .trim();
  const decade = (id.era || "").match(/\d{4}s|\d{4}|mid.?century/i)?.[0] || "";
  const parts = [maker, id.item_type, decade].filter(Boolean);
  if (!maker && id.style) parts.push(id.style);
  return parts.join(" ").replace(/\s+/g, " ").trim() || (id.title || "");
}

function marketLinks(id) {
  const q = encodeURIComponent(marketQuery(id));
  return {
    highEnd: [
      { name: "Chairish", url: `https://www.chairish.com/search?q=${q}` },
      { name: "1stDibs", url: `https://www.1stdibs.com/search/?q=${q}` },
    ],
    rarity: [
      { name: "eBay", url: `https://www.ebay.com/sch/i.html?_nkw=${q}` },
      { name: "Etsy", url: `https://www.etsy.com/search?q=${q}` },
    ],
  };
}

// Live-search URL patterns per platform — tapping a thread opens actual comparable listings
// on the target platform (Google-Lens-style workflow, not AI-generated advice).
const PLATFORM_SEARCH_URLS = {
  "Chairish": (q) => `https://www.chairish.com/search?q=${encodeURIComponent(q)}`,
  "1stDibs": (q) => `https://www.1stdibs.com/search/?q=${encodeURIComponent(q)}`,
  "Pamono": (q) => `https://www.pamono.com/?q=${encodeURIComponent(q)}`,
  "AptDeco": (q) => `https://www.aptdeco.com/search?query=${encodeURIComponent(q)}`,
  "Etsy": (q) => `https://www.etsy.com/search?q=${encodeURIComponent(q)}`,
  "eBay": (q) => `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(q)}`,
  "Ruby Lane": (q) => `https://www.rubylane.com/search?q=${encodeURIComponent(q)}`,
};

function threadUrl(platform, thread) {
  const fn = PLATFORM_SEARCH_URLS[platform];
  return fn ? fn(thread) : null;
}

function MarketRow({ label, sub, links, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D]">{label} <span className="normal-case font-normal text-[#B8B2A6]">· {sub}</span></p>
      <div className="flex flex-wrap gap-2 mt-1.5">
        {links.map((l) => (
          <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer"
            data-testid={`market-link-${l.name.toLowerCase()}`}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-[#0D1B2A] rounded-md text-sm font-semibold hover:bg-[#0D1B2A] hover:text-[#FDFBF7] transition-colors">
            {l.name} <ArrowUpRight size={14} weight="bold" />
          </a>
        ))}
      </div>
    </div>
  );
}

function LockedTeaser({ navigate }) {
  return (
    <div className="mt-4 relative rounded-lg border border-[#0D1B2A] bg-[#FBFAF6] overflow-hidden" data-testid="find-locked-teaser">
      <div className="p-3 space-y-2.5 blur-[5px] select-none pointer-events-none" aria-hidden="true">
        <div className="inline-flex items-center gap-1.5 bg-[#0D1B2A] text-[#FDFBF7] text-xs font-bold px-2.5 py-1 rounded-full">
          <Storefront size={13} weight="fill" /> Best home: 1stDibs · 78% accepted
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="h-3 bg-[#E2DDD1] rounded w-2/5" />
          <div className="h-3 bg-[#E2DDD1] rounded w-1/4" />
        </div>
        <div className="h-3 bg-[#E2DDD1] rounded w-3/5" />
        <div className="h-3 bg-[#E2DDD1] rounded w-1/2" />
      </div>
      <div className="absolute inset-0 bg-[#FBFAF6]/55 backdrop-blur-[1px] flex flex-col items-center justify-center text-center px-4">
        <LockSimple size={22} weight="fill" className="text-[#B47A3C]" />
        <p className="text-sm font-bold text-[#0D1B2A] mt-1 leading-tight">Where it sells · price range · authenticity checks</p>
        <p className="text-xs text-[#57534E] mt-1 max-w-xs leading-snug">
          Naming it is the easy part. Unlock the best-fit marketplace, the resale range, and reproduction
          red-flags — so you're not stuck researching and typing for hours.
        </p>
        <button onClick={() => navigate("/plans")} data-testid="find-unlock-button"
          className="mt-2.5 inline-flex items-center gap-1.5 h-9 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-semibold hover:bg-black transition-transform active:scale-95">
          <Crown size={14} weight="fill" /> Unlock with Member
        </button>
      </div>
    </div>
  );
}

const RATING_LABELS = {
  nailed: "Nailed it — exact match",
  solid: "Solid — enough to make a great buy",
  insufficient: "Not enough information to accurately determine the buy",
};

function FindCard({ f, onDelete, onBought, onPromote, onShare, onUnshare, onAddAngle, onRate, onRefineByMaker, analyzing, navigate }) {
  const id = f.identification || {};
  const rec = f.recommendation || {};
  const am = authMeta(id?.authenticity?.assessment);
  const AuthIcon = am.Icon;
  const [showDetail, setShowDetail] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [rateVerdict, setRateVerdict] = useState("");
  const [rateNote, setRateNote] = useState("");
  const [rateBusy, setRateBusy] = useState(false);
  const [makerInput, setMakerInput] = useState("");
  const [refineBusy, setRefineBusy] = useState(false);
  const rating = f.finder_rating;
  const links = marketLinks(id);
  const locked = !!f.locked;
  const angles = f.image_paths?.length ? f.image_paths : [f.storage_path];
  const hasMaker = id.likely_designer && !/not identified|unknown|^\s*$/i.test(id.likely_designer);
  // Lens-style restraint: only surface a maker when it's a confident, exact match.
  const isExactMatch = id.confidence === "High" && hasMaker;
  // Soften the authenticity wording ("Looks authentic" -> "Appears authentic") to leave room for error.
  const authText = (id?.authenticity?.assessment || "").replace(/^looks\b/i, "Appears");

  const submitRating = async () => {
    if (!rateVerdict || !onRate) return;
    setRateBusy(true);
    const ok = await onRate(f.id, rateVerdict, rateNote);
    setRateBusy(false);
    if (ok) {
      setShowRate(false);
      setRateVerdict("");
      setRateNote("");
    }
  };

  const submitMakerHint = async () => {
    const m = makerInput.trim();
    if (!m || !onRefineByMaker) return;
    setRefineBusy(true);
    const ok = await onRefineByMaker(f.id, m);
    setRefineBusy(false);
    if (ok) setMakerInput("");
  };

  const cancelRating = () => {
    setShowRate(false);
    setRateVerdict("");
    setRateNote("");
  };

  return (
    <div id={`find-card-${f.id}`} className="border border-[#0D1B2A] bg-white rounded-xl overflow-hidden shadow-sm" data-testid={`find-card-${f.id}`}>
      <div className="grid sm:grid-cols-[200px_1fr]">
        <div className="bg-[#F1EFE9] aspect-square sm:aspect-auto relative">
          <img src={fileUrl(f.storage_path)} alt={id.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/vv_logo.jpg"; e.currentTarget.className = "w-full h-full object-contain opacity-30 p-6"; }} className="w-full h-full object-cover" data-testid={`find-photo-${f.id}`} />
          {angles.length > 1 ? (
            <span className="absolute bottom-2 left-2 bg-[#0D1B2A]/80 text-[#FDFBF7] text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid={`find-anglecount-${f.id}`}>
              {angles.length} angles
            </span>
          ) : null}
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold text-[#57534E] leading-tight" data-testid={`find-title-${f.id}`}>{id.title || "Vintage find"}</h3>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => shareFindImage(f.id, id.title)} data-testid={`find-shareimg-${f.id}`} className="text-[#B47A3C] hover:text-[#6B4420] transition-colors" aria-label="Share image" title="Share as image">
                <ShareNetwork size={18} weight="fill" />
              </button>
              <button onClick={() => onDelete(f.id)} data-testid={`find-delete-${f.id}`} className="text-[#B8B2A6] hover:text-[#B91C1C] transition-colors" aria-label="Remove find">
                <Trash size={18} />
              </button>
            </div>
          </div>

          {/* Best-fit marketplace badge — VV's differentiator, one compact line (Member only) */}
          {!locked && f.placement?.available ? (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-[#0D1B2A] text-[#FDFBF7] text-xs font-bold px-2.5 py-1 rounded-full" data-testid={`find-placement-${f.id}`}>
              <Storefront size={13} weight="fill" /> Best home: {f.placement.platform}
              <span className="font-normal text-[#D6D3CD]">· {f.placement.acceptance_rate}% accepted{f.placement.basis === "category" ? " for pieces like this" : ""}</span>
            </div>
          ) : null}

          {/* Hero: lead with Era. Maker shows ONLY on an exact (high-confidence) match — Lens-style restraint. */}
          <div className={`mt-3 grid ${isExactMatch ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D]">Era</p>
              <p className="text-2xl font-extrabold tracking-tight text-[#0D1B2A] leading-tight" data-testid={`find-era-${f.id}`}>{id.era || "—"}</p>
            </div>
            {isExactMatch ? (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D]">Maker</p>
                <p className="text-lg font-bold text-[#0D1B2A] leading-tight" data-testid={`find-maker-${f.id}`}>{id.likely_designer}</p>
              </div>
            ) : null}
          </div>

          {/* Identity essentials — Nichole's North Star: Era → Style → Provenance → Maker → Live comps → Dimensions */}
          <div className="mt-3 space-y-2">
            {id.style ? <p className="text-sm text-[#57534E]" data-testid={`find-style-${f.id}`}><span className="text-[#8A867D]">Style:</span> {id.style}</p> : null}

            {/* Provenance — country of origin + attribution basis + any label hints (compact chip row) */}
            {(id.provenance?.country_of_origin || id.provenance?.attribution_basis || id.provenance?.label_hints?.length) ? (
              <div className="flex flex-wrap items-center gap-1.5" data-testid={`find-provenance-${f.id}`}>
                <span className="text-[#8A867D] text-sm">Provenance:</span>
                {id.provenance?.country_of_origin ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F1EFE9] border border-[#D6D3CD] rounded-full text-xs font-semibold text-[#33302B]" data-testid={`find-country-${f.id}`}>
                    {id.provenance.country_of_origin}
                  </span>
                ) : null}
                {id.provenance?.attribution_basis ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FDF8EC] border border-[#E3B341]/50 rounded-full text-xs font-semibold text-[#33302B]" data-testid={`find-attribution-${f.id}`}>
                    {id.provenance.attribution_basis}
                  </span>
                ) : null}
                {id.provenance?.label_hints?.length ? (
                  <span className="text-xs text-[#8A867D] italic" data-testid={`find-labelhints-${f.id}`}>{id.provenance.label_hints.join(" · ")}</span>
                ) : null}
              </div>
            ) : null}

            <Chips items={id.materials} testid={`find-materials-${f.id}`} />
            {id.summary ? (() => {
              // Bullet-ify: if the AI returned newline-separated bullets, render as a list. Fallback to paragraph.
              const bullets = id.summary.split(/\r?\n/).map((s) => s.replace(/^[\s\-\*\u2022]+/, "").trim()).filter(Boolean);
              return bullets.length > 1 ? (
                <ul className="list-disc pl-5 space-y-0.5" data-testid={`find-summary-${f.id}`}>
                  {bullets.map((b, i) => <li key={i} className="text-sm text-[#57534E] leading-snug">{b}</li>)}
                </ul>
              ) : (
                <p className="text-sm text-[#57534E] leading-relaxed" data-testid={`find-summary-${f.id}`}>{id.summary}</p>
              );
            })() : null}
          </div>

          {/* Authenticity read — kept visible, softened wording */}
          {!locked && id?.authenticity?.assessment ? (
            <span className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${am.cls}`} data-testid={`find-auth-${f.id}`}>
              <AuthIcon size={13} weight="fill" /> {authText}
            </span>
          ) : null}

          {/* Maker's-mark cue — when confidence is Low/Medium AND no maker attribution yet.
              Google-Lens-style refinement: dealer reads the mark with their eyes and TYPES it in
              (typing is a stronger signal than adding more photos, which tend to confuse the AI). */}
          {!locked && !isExactMatch && (id.confidence === "Low" || id.confidence === "Medium") && !hasMaker ? (
            <div className="mt-3 p-3 bg-[#FEF7E4] border border-[#E3B341]/50 rounded-md" data-testid={`find-makers-mark-cue-${f.id}`}>
              <div className="flex items-start gap-2.5 mb-2.5">
                <MagnifyingGlass size={16} weight="fill" className="text-[#B47A3C] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#0D1B2A] leading-tight">Found a maker's mark? Type what you see.</p>
                  <p className="text-xs text-[#57534E] mt-0.5 leading-snug">Most common spots: underside of the seat, back of the frame, inside a drawer, bottom of a lamp base. Typing beats snapping more photos — the AI locks in on the maker instantly.</p>
                </div>
              </div>
              <div className="flex items-stretch gap-1.5">
                <input type="text" value={makerInput} onChange={(e) => setMakerInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && makerInput.trim() && !refineBusy) submitMakerHint(); }}
                  placeholder="e.g. Ethan Allen, Herman Miller, Baker"
                  data-testid={`find-maker-input-${f.id}`}
                  disabled={refineBusy}
                  className="flex-1 h-10 px-3 border border-[#0D1B2A]/30 bg-white text-sm font-medium text-[#0D1B2A] rounded-md focus:border-[#0D1B2A] outline-none placeholder:text-[#B8B4AC] disabled:opacity-60" />
                <button onClick={submitMakerHint} disabled={!makerInput.trim() || refineBusy}
                  data-testid={`find-maker-submit-${f.id}`}
                  className="h-10 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-bold hover:bg-black transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 whitespace-nowrap">
                  {refineBusy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={13} weight="fill" />} {refineBusy ? "Sharpening…" : "Sharpen the ID"}
                </button>
              </div>
              <button onClick={() => onAddAngle(f.id)} data-testid={`find-makers-mark-add-${f.id}`}
                className="mt-2 text-xs text-[#8A867D] hover:text-[#0D1B2A] underline">
                Or add another photo instead
              </button>
            </div>
          ) : null}

          {/* Free scouts see a blurred teaser (money layer stripped server-side) */}
          {locked ? <LockedTeaser navigate={navigate} /> : null}

          {/* Everything else tucked into an on-demand dropdown — don't overwhelm the seller (Member only) */}
          {!locked ? (
            <>
              {/* PROMOTED: See live comparables — primary field #5 from Nichole's North Star.
                  Tapping opens each platform's search page for the AI's query — no clicks wasted inside VV. */}
              {id.platform_match_threads?.length ? (
                <div className="mt-4" data-testid={`find-threads-${f.id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#B47A3C] mb-1 flex items-center gap-1"><MagnifyingGlass size={12} weight="fill" /> See live comparables</p>
                  <p className="text-[11px] text-[#8A867D] mb-1.5 italic leading-tight">Tap to see what dealers are actually asking on each platform — the market prices it, not us.</p>
                  <div className="flex flex-col gap-1.5">
                    {id.platform_match_threads.slice(0, 4).map((t, i) => {
                      const url = threadUrl(t.platform, t.thread);
                      const inner = (
                        <>
                          <MagnifyingGlass size={12} weight="bold" className="text-[#B47A3C]" />
                          <span className="font-bold min-w-[68px]">{t.platform}</span>
                          <span className="text-[#57534E] flex-1 truncate">"{t.thread}"</span>
                          {url ? <ArrowUpRight size={12} weight="bold" className="text-[#8A867D] flex-shrink-0" /> : null}
                        </>
                      );
                      return url ? (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                          data-testid={`find-thread-link-${f.id}-${i}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-[#0D1B2A]/25 text-xs text-[#33302B] rounded-none hover:border-[#0D1B2A] hover:bg-[#FBFAF6] transition-colors">
                          {inner}
                        </a>
                      ) : (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#F1EFE9] border border-[#D6D3CD] text-xs text-[#33302B] rounded-none">
                          {inner}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* PROMOTED: Listing measurements — the 90% time-saver Nichole flagged as the "holy grail". */}
              {id.dimensions?.measurements?.length ? (
                <div className="mt-4" data-testid={`find-dimensions-${f.id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#B47A3C] mb-1 flex items-center gap-1.5">
                    Listing measurements
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-none border ${id.dimensions.source === "documented" ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" : id.dimensions.source === "typical_for_form" ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" : "bg-[#F1EFE9] text-[#57534E] border-[#D6D3CD]"}`}>
                      {id.dimensions.source === "documented" ? "Documented" : id.dimensions.source === "typical_for_form" ? "Typical range" : "Estimated"}
                    </span>
                  </p>
                  <div className="border border-[#E7E3DA] rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {id.dimensions.measurements.map((m, i) => (
                          <tr key={i} data-testid={`find-dim-row-${f.id}-${i}`} className={`border-b border-[#F0EFEA] last:border-b-0 ${i % 2 ? "bg-[#FBFAF6]" : "bg-white"}`}>
                            <td className="px-2.5 py-1.5 text-[#57534E] font-medium whitespace-nowrap">{m.label}</td>
                            <td className="px-2.5 py-1.5 text-[#0D1B2A] font-bold text-right whitespace-nowrap">{m.value}</td>
                            {m.notes ? <td className="px-2.5 py-1.5 text-[#8A867D] text-xs italic hidden sm:table-cell">{m.notes}</td> : <td className="hidden sm:table-cell" />}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-[#8A867D] italic mt-1">
                    {id.dimensions.source === "documented"
                      ? "From documented specs for this maker/model — always verify with a tape measure before listing."
                      : "Typical range for this form — always verify with a tape measure before listing."}
                  </p>
                </div>
              ) : null}

              <button onClick={() => setShowDetail((s) => !s)} data-testid={`find-detail-toggle-${f.id}`}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420]">
                {showDetail ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />} {showDetail ? "Hide drafting details" : "More drafting details"}
              </button>
              {showDetail ? (
                <div className="mt-3 space-y-3" data-testid={`find-detail-${f.id}`}>
                  {/* Live market look-ups */}
                  <div className="rounded-lg bg-[#FBFAF6] border border-[#E7E3DA] p-3 space-y-3" data-testid={`find-market-${f.id}`}>
                    <MarketRow label="What it could fetch" sub="high-end resale" links={links.highEnd} testid={`find-market-highend-${f.id}`} />
                    <MarketRow label="How common is it?" sub="rarity check" links={links.rarity} testid={`find-market-rarity-${f.id}`} />
                    <p className="text-[11px] text-[#8A867D]">Opens a live search on each site — tap through to eyeball the going rate. Nothing here is a guaranteed price.</p>
                  </div>

                  {/* Possible makers — only when we did NOT get an exact match */}
                  {!isExactMatch && id.possible_makers?.length ? (
                    <p className="text-sm text-[#57534E]" data-testid={`find-possible-makers-${f.id}`}>
                      <span className="text-[#8A867D]">Possible makers:</span> {id.possible_makers.join(", ")}
                    </p>
                  ) : null}

                  {/* Authenticity depth */}
                  {id?.authenticity?.red_flags?.length ? (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D] mb-1 flex items-center gap-1"><WarningCircle size={12} /> Watch for reproductions</p>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {id.authenticity.red_flags.slice(0, 5).map((r, i) => <li key={i} className="text-sm text-[#33302B]">{r}</li>)}
                      </ul>
                    </div>
                  ) : null}
                  {id?.authenticity?.authentication_tips?.length ? (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D] mb-1 flex items-center gap-1"><CheckCircle size={12} /> How to confirm</p>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {id.authenticity.authentication_tips.slice(0, 4).map((r, i) => <li key={i} className="text-sm text-[#57534E]">{r}</li>)}
                      </ul>
                    </div>
                  ) : null}

                </div>
              ) : null}

              {/* Finder rating — Member perk. Contributes to metadata sharpening over time. */}
              <div className="mt-3" data-testid={`find-rating-block-${f.id}`}>
                {rating?.verdict ? (
                  <div className="text-xs text-[#57534E] leading-relaxed" data-testid={`find-rating-badge-${f.id}`}>
                    <span className="text-[#8A867D]">You rated:</span>{" "}
                    <span className="font-bold text-[#0D1B2A]">{RATING_LABELS[rating.verdict] || rating.verdict}</span>
                    {" — "}
                    <span className="italic text-[#8A867D]">this shapes future scans.</span>{" "}
                    <button onClick={() => { setShowRate(true); setRateVerdict(rating.verdict); setRateNote(rating.note || ""); }}
                      data-testid={`find-rating-change-${f.id}`}
                      className="font-semibold text-[#B47A3C] hover:text-[#6B4420] underline">Change</button>
                  </div>
                ) : (
                  <button onClick={() => setShowRate((s) => !s)} data-testid={`find-rating-toggle-${f.id}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420]">
                    {showRate ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />} Rate this scan
                  </button>
                )}

                {showRate ? (
                  <div className="mt-2 border border-[#0D1B2A]/25 bg-[#FBFAF6] p-3 rounded-md space-y-2.5" data-testid={`find-rating-panel-${f.id}`}>
                    <p className="text-[11px] text-[#57534E] leading-snug italic">This is how the Finder gets sharper — your call shapes future scans.</p>
                    <div className="space-y-1.5">
                      {[
                        { key: "nailed", label: RATING_LABELS.nailed },
                        { key: "solid", label: RATING_LABELS.solid },
                        { key: "insufficient", label: RATING_LABELS.insufficient },
                      ].map((opt) => (
                        <button key={opt.key} onClick={() => setRateVerdict(opt.key)}
                          data-testid={`find-rating-${opt.key}-${f.id}`}
                          className={`w-full text-left px-3 py-2 border text-sm font-semibold transition-colors ${rateVerdict === opt.key ? "bg-[#0D1B2A] text-[#FDFBF7] border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#0D1B2A]/25 hover:border-[#0D1B2A]"}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {rateVerdict === "insufficient" ? (
                      <textarea value={rateNote} onChange={(e) => setRateNote(e.target.value)}
                        data-testid={`find-rating-note-${f.id}`}
                        rows={2} maxLength={500}
                        placeholder="What was missing? (optional — e.g. missed the maker's mark on the underside)"
                        className="w-full border border-[#0D1B2A]/25 rounded-none px-2.5 py-2 text-sm focus:border-[#0D1B2A] outline-none resize-none" />
                    ) : null}
                    <div className="flex items-center gap-2">
                      <button onClick={submitRating} disabled={!rateVerdict || rateBusy}
                        data-testid={`find-rating-submit-${f.id}`}
                        className="h-10 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-semibold hover:bg-black transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                        {rateBusy ? "Saving…" : "Submit rating"}
                      </button>
                      <button onClick={cancelRating} data-testid={`find-rating-cancel-${f.id}`}
                        className="text-sm font-semibold text-[#8A867D] hover:text-[#0D1B2A]">Cancel</button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}

          {/* Subtle add-angle — no nagging; only pre-purchase */}
          {!f.purchased && !f.promoted_listing_id ? (
            <div className="mt-3">
              <button onClick={() => onAddAngle(f.id)} disabled={analyzing} data-testid={`find-addangle-${f.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420] disabled:opacity-70">
                {analyzing ? <><Spinner size={14} className="animate-spin" /> Re-checking…</> : <><Camera size={14} weight="fill" /> Add another angle for a sharper read</>}
              </button>
            </div>
          ) : null}

          {/* Purchased reminder / actions */}
          {f.promoted_listing_id ? (
            <button onClick={() => navigate(`/review/${f.promoted_listing_id}`)} data-testid={`find-open-draft-${f.id}`}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#166534] hover:underline">
              <Storefront size={15} weight="fill" /> In your Drafts — open to finish <ArrowRight size={14} />
            </button>
          ) : f.purchased ? (
            <div className="mt-6 rounded-lg border border-[#FCD34D] bg-[#FFFBEB] p-3" data-testid={`find-purchased-reminder-${f.id}`}>
              <p className="text-sm font-semibold text-[#92400E] mb-2">You bought this — let's put it in your Drafts to build the listing.</p>
              <button onClick={() => onBought({ ...f, __alreadyMine: true })} data-testid={`find-promote-${f.id}`}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-semibold hover:bg-black transition-transform active:scale-95 text-center leading-tight">
                <CheckCircle size={16} weight="fill" className="shrink-0" /> This find is already mine! Send it to my Drafts
              </button>
            </div>
          ) : (
            <div className="mt-6">
              <button onClick={() => onBought(f)} data-testid={`find-bought-${f.id}`}
                className="w-full inline-flex items-center justify-center gap-2 h-12 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm sm:text-base font-semibold hover:bg-black transition-transform active:scale-[0.99] text-center leading-tight">
                <CheckCircle size={17} weight="fill" className="shrink-0" /> Fantastic find acquired! Send it to my Drafts
              </button>
            </div>
          )}

          {/* Share to the public Fantastic Finds gallery */}
          {(f.purchased || f.promoted_listing_id) ? (
            <div className="mt-3 pt-3 border-t border-[#F1EFE9]">
              {f.shared ? (
                <div className="flex items-center justify-between gap-2 flex-wrap" data-testid={`find-shared-${f.id}`}>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#166534]">
                    <Sparkle size={14} weight="fill" /> Shared to Fantastic Finds
                  </span>
                  <button onClick={() => onUnshare(f.id)} data-testid={`find-unshare-${f.id}`}
                    className="text-xs font-semibold text-[#8A867D] hover:text-[#B91C1C]">Remove from gallery</button>
                </div>
              ) : (
                <button onClick={() => onShare(f)} data-testid={`find-share-${f.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420]">
                  <Sparkle size={14} weight="fill" /> Show off your score in the gallery
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
